import { useEffect, useMemo, useState } from 'react'
import { fetchPermissionRequestMintingAuthority } from '../../api/ws'
import type {
  PermissionRequest,
  PermissionDecisionBehavior,
  ExternalResourceInvokeSubject,
  PickSubject,
  EntityCRUDSubject,
} from '../../store/types'
import { useAppStore } from '../../store/app'
import { CountdownTimer } from './CountdownTimer'
import { ToolInput } from './ToolInput'
import { ExternalResourceInvokeBody } from './ExternalResourceInvokeBody'
import { EntityCRUDBody } from './EntityCRUDBody'
import { PickBody, pickMetaString } from './PickBody'
import { AlwaysAllowMenu } from './AlwaysAllowMenu'
import { toolIcon } from './toolIcon'
import './PermissionRequestCard.css'

export function PermissionRequestCard({
  request,
  onShortcut,
  onAlwaysAllow,
  onAlwaysDeny,
}: {
  request: PermissionRequest
  onShortcut: (
    behavior: PermissionDecisionBehavior,
    extras?: { selectedOptionID?: string; freeText?: string },
  ) => void
  onAlwaysAllow: (expiresAt: string | null) => void
  onAlwaysDeny: (expiresAt: string | null) => void
}) {
  // Display fields prefer the server-enriched payload over the
  // device's per-org caches. Cross-org permission_requests (agent
  // lives in an org other than the user's current org) have no
  // entries in agentInstances / organizationPositions because the
  // WS subscriptions only carry the current-org roster. The server-
  // populated agent_name / position_name / organization_name fields
  // (see docs/cross-org-permission-rendering-plan.md) close that
  // gap. Cache lookup remains as fallback for older servers that
  // don't populate the embedded fields.
  const cachedPosition = useAppStore((s) =>
    s.organizationPositions.find((p) => p.id === request.organization_position_id),
  )
  const cachedAgent = useAppStore((s) =>
    s.agentInstances.find((a) => a.id === request.ai_agent_instance_id),
  )
  const currentOrgID = useAppStore((s) => s.organizationID)
  const agentName = request.agent_name ?? cachedAgent?.name
  const positionName = request.position_name ?? cachedPosition?.name
  const orgName = request.organization_name
  const orgID = request.organization_id
  const isCrossOrg = orgID !== undefined && currentOrgID !== undefined && orgID !== currentOrgID
  const outsideProject = useMemo(
    () => isOutsideProject(request),
    [request],
  )

  // subject_kind drives the body renderer. Card chrome — countdown,
  // approve/deny buttons, always-allow menu — is shared but optional
  // dispositions on picks (deny / always_allow / deny_stop) are
  // gated on subject.available_responses.
  const isExternalResource = request.subject_kind === 'external_resource_invoke'
  const isPick = request.subject_kind === 'pick'
  const isEntityCRUD = request.subject_kind === 'entity_crud'
  const extSubject = isExternalResource
    ? (request.subject as ExternalResourceInvokeSubject)
    : null
  const pickSubject = isPick ? (request.subject as PickSubject) : null
  const entitySubject = isEntityCRUD ? (request.subject as EntityCRUDSubject) : null

  // Card-owned: which option the user has selected, and any free-text
  // input. For picks where the server-side handler can identify a
  // sensible default (binding's is_default variant ⇒ intent='primary'),
  // pre-select it so a single Allow tap works when the answer is obvious.
  const [selectedOptionID, setSelectedOptionID] = useState<string | null>(
    () => pickSubject?.options.find((o) => o.intent === 'primary')?.id ?? null,
  )
  const [freeText, setFreeText] = useState('')

  const headerIcon = isEntityCRUD
    ? '🏠'
    : isExternalResource || isPick
      ? '🔌'
      : toolIcon(request.tool_name)
  const headerLabel = isEntityCRUD
    ? 'Household change'
    : isExternalResource
      ? `${extSubject!.plugin_slug}.${extSubject!.verb}`
      : isPick
        ? pickHeaderLabel(pickSubject!)
        : request.tool_name

  // Picks: Allow is gated on a non-empty selection OR (allow_free_text
  // AND freeText non-empty). For non-picks the Allow button is always
  // enabled (legacy semantics).
  const allowDisabled = isPick && !selectedOptionID && !(pickSubject!.allow_free_text && freeText.trim())

  // Mint-rule authority gate (phase 4b): the server's
  // permission_request_minting_authority_check tells us whether
  // tapping Always Allow would actually mint a rule or degrade to
  // plain allow. Hide the button when the responder lacks authority
  // so users don't tap a button that quietly no-ops the rule write.
  // Optimistic-default to true: render the button while the check
  // is in flight; the server-side gate is the load-bearing
  // enforcement (see docs/approver-resolution.md §"Rule-minting
  // authority"). Errors leave the optimistic state in place — the
  // server will still enforce, and the worst case is a tap that
  // degrades silently.
  const [canMintAllow, setCanMintAllow] = useState(true)
  // can_deny defaults FALSE — Always Deny is the destructive direction
  // and shouldn't render optimistically; only show when the server
  // confirms minting authority. Allow defaults true (existing behavior)
  // because the worst case is a tap that silently degrades to plain
  // allow, which is benign.
  const [canMintDeny, setCanMintDeny] = useState(false)
  useEffect(() => {
    let cancelled = false
    fetchPermissionRequestMintingAuthority(request.id)
      .then((r) => {
        if (cancelled) return
        if (typeof r.can_allow === 'boolean') setCanMintAllow(r.can_allow)
        if (typeof r.can_deny === 'boolean') setCanMintDeny(r.can_deny)
      })
      .catch(() => {
        // Leave the optimistic default in place. Server gate still applies.
      })
    return () => {
      cancelled = true
    }
  }, [request.id])

  // Picks suppress AlwaysAllow / AlwaysDeny / Deny&Stop unless the
  // server explicitly listed them on available_responses. Non-picks
  // keep the legacy chrome. Additionally hide when the responder
  // lacks mint-rule authority.
  const showAlwaysAllow =
    canMintAllow && (!isPick || pickSubject!.available_responses.includes('always_allow'))
  const showAlwaysDeny =
    canMintDeny && (!isPick || pickSubject!.available_responses.includes('always_deny'))
  const showDenyStop = !isPick || pickSubject!.available_responses.includes('deny_stop')

  // Show the plain Deny button when the pick declares 'deny' (most
  // picks do) OR when we're not on a pick at all. Picks that omit
  // 'deny' from available_responses (e.g. confirm_destructive where
  // Cancel lives in options) hide it.
  const showDeny = !isPick || pickSubject!.available_responses.includes('deny')

  return (
    <div className="pr-card">
      <div className="pr-card__badges">
        {positionName && !isExternalResource && !isPick && (
          <span className="pr-card__badge pr-card__badge--position">{positionName}</span>
        )}
        {agentName && (
          <span className="pr-card__badge pr-card__badge--agent">{agentName}</span>
        )}
        {isCrossOrg && orgName && (
          <span className="pr-card__badge pr-card__badge--org">{orgName}</span>
        )}
        {isExternalResource && (
          <span className="pr-card__badge pr-card__badge--position">
            {extSubject!.connection_id}
          </span>
        )}
        {isPick && pickMetaString(pickSubject!, 'connection_display_name') && (
          <span className="pr-card__badge pr-card__badge--position">
            {pickMetaString(pickSubject!, 'connection_display_name')}
          </span>
        )}
        {outsideProject && (
          <span className="pr-card__badge pr-card__badge--outside">⚠ Outside project</span>
        )}
        {request.source_label && (
          <span
            className="pr-card__badge pr-card__badge--source"
            title="This request came from a shared voice device — not from your phone."
          >
            🔊 {request.source_label}
          </span>
        )}
      </div>

      <div className="pr-card__header">
        <div className="pr-card__icon" aria-hidden>
          {headerIcon}
        </div>
        <div className="pr-card__tool">{headerLabel}</div>
        {request.parked ? (
          <span className="pr-card__parked">⏰ Routine · won't time out</span>
        ) : (
          <CountdownTimer request={request} onExpire={() => onShortcut('deny')} />
        )}
      </div>

      <div className="pr-card__scroll">
        {isExternalResource ? (
          <ExternalResourceInvokeBody subject={extSubject!} />
        ) : isEntityCRUD ? (
          <EntityCRUDBody subject={entitySubject!} />
        ) : isPick ? (
          <>
            <PickBody
              subject={pickSubject!}
              selectedOptionID={selectedOptionID}
              freeText={freeText}
              onSelectOption={setSelectedOptionID}
              onChangeFreeText={setFreeText}
            />
          </>
        ) : (
          <ToolInput request={request} />
        )}
      </div>

      <div className="pr-card__actions">
        {showAlwaysAllow && <AlwaysAllowMenu onChoose={onAlwaysAllow} />}
        {showAlwaysDeny && (
          <AlwaysAllowMenu decision="deny" onChoose={onAlwaysDeny} />
        )}
        <div className="pr-card__row">
          {showDeny && (
            <button
              className="pr-card__btn pr-card__btn--deny"
              onClick={() => onShortcut('deny')}
            >
              ✕ Deny
            </button>
          )}
          <button
            className="pr-card__btn pr-card__btn--allow"
            disabled={allowDisabled}
            onClick={() =>
              onShortcut(
                'allow',
                isPick
                  ? {
                      selectedOptionID: selectedOptionID ?? undefined,
                      freeText: freeText.trim() || undefined,
                    }
                  : undefined,
              )
            }
          >
            ✓ Allow
          </button>
        </div>
        {showDenyStop && (
          <button
            className="pr-card__btn pr-card__btn--stop"
            onClick={() => onShortcut('deny_stop')}
          >
            ✋ Deny & Stop
          </button>
        )}
      </div>
    </div>
  )
}

function isOutsideProject(req: PermissionRequest): boolean {
  // Server marks this on the request envelope; falls back to false when
  // the server doesn't include the flag. Matches PermissionRequest.outsideProject
  // on the native side.
  const raw = (req as unknown as { outside_project?: boolean }).outside_project
  return raw === true
}

function pickHeaderLabel(s: PickSubject): string {
  switch (s.pick_kind) {
    case 'confirm_destructive':
      return 'Confirm'
    default:
      return s.pick_kind
  }
}
